import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusPanel from "@/components/StatusPanel";
import ConnectionInstructions from "@/components/ConnectionInstructions";
import ConfigPanel from "@/components/ConfigPanel";
import ChannelsTable from "@/components/ChannelsTable";
import ActivityLog from "@/components/ActivityLog";
import { Server, LogOut } from "lucide-react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadAll = useCallback(async () => {
    const [s, c] = await Promise.all([api.get("/admin/status"), api.get("/admin/config")]);
    setStatus(s.data);
    setConfig(c.data);
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 8000);
    return () => clearInterval(t);
  }, [loadAll]);

  const handleSaved = () => {
    loadAll();
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="min-h-screen bg-zinc-950 bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:16px_16px]">
      <header className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-emerald-500/30 bg-emerald-500/10 drop-shadow-[0_0_8px_rgba(16,185,129,0.25)]">
              <Server className="h-4 w-4 text-emerald-400" />
            </div>
            <div>
              <h1 className="font-heading text-base font-bold tracking-tight text-zinc-50">
                {status?.server_name || "IPTV Bridge"}
              </h1>
              <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                Emby-Compatible Server
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-xs text-zinc-500 sm:block">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-sm border border-zinc-800 bg-zinc-900 px-3 py-1.5 font-mono text-xs uppercase text-zinc-300 transition-colors duration-200 hover:border-red-500/40 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <StatusPanel status={status} />
          </div>
          <div className="lg:col-span-8">
            <ConnectionInstructions />
          </div>

          <div className="lg:col-span-12">
            <ConfigPanel config={config} onSaved={handleSaved} />
          </div>

          <div className="lg:col-span-8">
            <div className="h-[520px]">
              <ChannelsTable refreshKey={refreshKey} />
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="h-[520px]">
              <ActivityLog refreshKey={refreshKey} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
